import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Trash2,
  Loader2,
  ChevronDown,
  X,
  Save,
  Search,
  Plus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../api/api";
import MainLayout from "../sidebar/Sidebar.js";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export default function CostCatalog() {
  const queryClient = useQueryClient();
  const [location, setLocation] = useState("Cost Catalog");
  const [searchTerm, setSearchTerm] = useState("");
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  // Input row state for adding new items
  const [newItem, setNewItem] = useState({
    name: "",
    code: "",
    default_quantity: "",
    unit: "",
    unit_price: "",
    category: "",
  });
  const [isInputRowActive, setIsInputRowActive] = useState(false);
  const inputRowRef = useRef(null);

  // Fetch catalog items
  const {
    data: catalogItems = [],
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ["costCatalog"],
    queryFn: async () => {
      const response = await api.get("/cost-catalog/");
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (itemData) => {
      const response = await api.post("/cost-catalog/", itemData);
      return response.data;
    },
    onSuccess: (newItem) => {
      queryClient.setQueryData(["costCatalog"], (old) => [...(old || []), newItem]);
      setNewItem({
        name: "",
        code: "",
        default_quantity: "",
        unit: "",
        unit_price: "",
        category: "",
      });
      setSuccessMessage("Item added to catalog");
      setTimeout(() => setSuccessMessage(""), 3000);
    },
    onError: (err) => {
      setError(err.response?.data?.detail || "Failed to add item");
      setTimeout(() => setError(""), 5000);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ itemId, itemData }) => {
      const response = await api.put(`/cost-catalog/${itemId}`, itemData);
      return response.data;
    },
    onSuccess: (updatedItem) => {
      queryClient.setQueryData(["costCatalog"], (old) =>
        (old || []).map((item) => (item.id === updatedItem.id ? updatedItem : item))
      );
      setShowEditPanel(false);
      setEditingItem(null);
      setSuccessMessage("Item updated successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    },
    onError: (err) => {
      setError(err.response?.data?.detail || "Failed to update item");
      setTimeout(() => setError(""), 5000);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (itemId) => {
      await api.delete(`/cost-catalog/${itemId}`);
      return itemId;
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(["costCatalog"], (old) =>
        (old || []).filter((item) => item.id !== deletedId)
      );
      setSuccessMessage("Item deleted");
      setTimeout(() => setSuccessMessage(""), 3000);
    },
    onError: (err) => {
      setError(err.response?.data?.detail || "Failed to delete item");
      setTimeout(() => setError(""), 5000);
    },
  });

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return catalogItems;
    const term = searchTerm.toLowerCase();
    return catalogItems.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        (item.code && item.code.toLowerCase().includes(term)) ||
        (item.category && item.category.toLowerCase().includes(term))
    );
  }, [catalogItems, searchTerm]);

  // Handle adding new item
  const handleAddItem = useCallback(() => {
    if (!newItem.name.trim()) return;

    createMutation.mutate({
      name: newItem.name.trim(),
      code: newItem.code.trim() || null,
      default_quantity: newItem.default_quantity ? parseInt(newItem.default_quantity, 10) : 1,
      unit: newItem.unit.trim() || null,
      unit_price: newItem.unit_price.trim() || null,
      category: newItem.category || null,
    });
  }, [newItem, createMutation]);

  // Handle click outside input row
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRowRef.current && !inputRowRef.current.contains(event.target)) {
        if (newItem.name.trim()) {
          handleAddItem();
        }
        setIsInputRowActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [newItem, handleAddItem]);

  const handleDelete = (item) => {
    deleteMutation.mutate(item.id);
  };

  const handleToggleSelect = (index) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length && filteredItems.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map((_, i) => i)));
    }
  };

  const handleEditItem = (item) => {
    setEditingItem({ ...item });
    setShowEditPanel(true);
  };

  const handleSaveEdit = () => {
    if (!editingItem.name.trim()) {
      setError("Item name cannot be empty");
      return;
    }

    updateMutation.mutate({
      itemId: editingItem.id,
      itemData: {
        name: editingItem.name.trim(),
        code: editingItem.code?.trim() || null,
        default_quantity: editingItem.default_quantity || 1,
        unit: editingItem.unit?.trim() || null,
        unit_price: editingItem.unit_price?.trim() || null,
        category: editingItem.category || null,
      },
    });
  };

  const handleCancelEdit = () => {
    setShowEditPanel(false);
    setEditingItem(null);
    setError("");
  };

  const handleDeleteSelected = () => {
    const itemsToDelete = Array.from(selectedItems).map((index) => filteredItems[index]);
    itemsToDelete.forEach((item) => deleteMutation.mutate(item.id));
    setSelectedItems(new Set());
  };

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="flex flex-col h-full overflow-hidden" style={{ backgroundColor: 'rgba(0, 109, 226, 0)' }}>
        <div className="flex-1 overflow-hidden py-6 px-5 min-h-0">
          <div className="h-full flex flex-col overflow-hidden">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm mb-6" style={{ color: '#1C1C1C' }}>
                {error}
              </div>
            )}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm mb-6" style={{ color: '#1C1C1C' }}>
                {successMessage}
              </div>
            )}
            {fetchError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm mb-6" style={{ color: '#1C1C1C' }}>
                Failed to load catalog items. Please refresh the page.
              </div>
            )}

            <div className="min-h-0 flex-1 flex flex-col overflow-hidden" style={{ maxHeight: '100%' }}>
              {/* Header with search and actions */}
              <div className="flex items-center justify-between gap-3 pb-3 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="relative min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: "#727272" }} />
                    <input
                      type="text"
                      placeholder="Search catalog..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 rounded-lg bg-white text-sm shadow-sm focus:outline-none focus:ring-0"
                      style={{ border: "1px solid #e8e8e8", color: "#727272", padding: "7px 11px", paddingLeft: "40px" }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {selectedItems.size > 0 && (
                    <Button variant="customStandard" size="customStandard" onClick={handleDeleteSelected}>
                      <Trash2 className="w-4 h-4" />
                      Delete Selected ({selectedItems.size})
                    </Button>
                  )}
                </div>
              </div>

              {/* Item count */}
              {catalogItems.length > 0 && (
                <div className="flex items-center gap-2 pb-3 flex-shrink-0">
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded-md" style={{ color: '#1C1C1C' }}>
                    {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
              )}

              {/* Loading state */}
              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              )}

              {/* Table */}
              {!isLoading && (
                <div className="min-h-0 overflow-y-auto rounded-2xl" style={{ boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.02), 0 -2px 12px -4px rgba(0, 0, 0, 0.02), 0 4px 16px -6px rgba(0, 0, 0, 0.06), 0 8px 20px -10px rgba(0, 0, 0, 0.04)', border: '1px solid rgba(0, 0, 0, 0.12)', maxHeight: '100%' }}>
                  <table className="w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)' }}>
                    <thead className="sticky top-0 z-10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                      <tr>
                        <th className="px-4 py-4 w-12">
                          <button
                            onClick={handleToggleSelectAll}
                            className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors p-0.5"
                            style={{
                              borderColor: selectedItems.size === filteredItems.length && filteredItems.length > 0 ? '#006de2' : '#d1d5db',
                              backgroundColor: 'transparent',
                            }}
                          >
                            {selectedItems.size === filteredItems.length && filteredItems.length > 0 && (
                              <div className="w-full h-full rounded" style={{ backgroundColor: '#006de2' }} />
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                          Item Name
                        </th>
                        <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                          Code
                        </th>
                        <th className="px-6 py-4 text-left font-medium uppercase tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                          Default QTY
                        </th>
                        <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                          Unit
                        </th>
                        <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                          Unit Price
                        </th>
                        <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                          Category
                        </th>
                        <th className="px-6 py-4 w-8"></th>
                      </tr>
                      <tr>
                        <th colSpan="8" className="p-0">
                          <div className="mx-4" style={{ borderBottom: '1px solid #e8e8e8' }} />
                        </th>
                      </tr>
                    </thead>
                    <tbody style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                      <AnimatePresence>
                        {filteredItems.map((item, index) => (
                          <React.Fragment key={item.id}>
                            <motion.tr
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2, delay: index * 0.02 }}
                              className="cursor-pointer group"
                              onClick={() => handleEditItem(item)}
                              onMouseEnter={() => setHoveredRowIndex(index)}
                              onMouseLeave={() => setHoveredRowIndex(null)}
                            >
                              <td className="py-4 pl-4 pr-0" style={{ position: 'relative' }}>
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: '0.5rem',
                                    right: 0,
                                    pointerEvents: 'none',
                                    zIndex: 0,
                                    borderRadius: '8px 0 0 8px',
                                    backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined,
                                    transition: 'background-color 0.15s ease',
                                  }}
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleSelect(index);
                                  }}
                                  className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors p-0.5"
                                  style={{
                                    borderColor: selectedItems.has(index) ? '#006de2' : '#d1d5db',
                                    backgroundColor: 'transparent',
                                    position: 'relative',
                                    zIndex: 1,
                                  }}
                                >
                                  {selectedItems.has(index) && (
                                    <div className="w-full h-full rounded" style={{ backgroundColor: '#006de2' }} />
                                  )}
                                </button>
                              </td>
                              <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                                {item.name}
                              </td>
                              <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                                {item.code || "-"}
                              </td>
                              <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                                {item.default_quantity || 1}
                              </td>
                              <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                                {item.unit || "-"}
                              </td>
                              <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                                {item.unit_price ? `$${item.unit_price}` : "-"}
                              </td>
                              <td className="px-6 py-4" style={{ backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                                <span className="text-sm" style={{ color: '#424242' }}>
                                  {item.category || "-"}
                                </span>
                              </td>
                              <td className="py-4 pr-4 pl-0" style={{ position: 'relative' }}>
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: '0.5rem',
                                    pointerEvents: 'none',
                                    zIndex: 0,
                                    borderRadius: '0 8px 8px 0',
                                    backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined,
                                    transition: 'background-color 0.15s ease',
                                  }}
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(item);
                                  }}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                  style={{ position: 'relative', zIndex: 1 }}
                                  title="Delete item"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </motion.tr>
                            <tr>
                              <td colSpan="8" className="p-0">
                                <div className="mx-4" style={{ borderBottom: '1px solid #e8e8e8' }} />
                              </td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </AnimatePresence>

                      {/* Input row for adding new items */}
                      <tr ref={inputRowRef} className="transition-colors">
                        <td className="px-4 py-4">
                          <div className="w-5 h-5 rounded-md border-2 border-gray-200" />
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            className="w-full bg-transparent text-sm focus:outline-none placeholder-gray-400"
                            style={{ color: '#424242' }}
                            placeholder="Add new item..."
                            value={newItem.name}
                            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newItem.name.trim()) {
                                handleAddItem();
                              }
                            }}
                            onFocus={() => setIsInputRowActive(true)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            className="w-full bg-transparent text-sm focus:outline-none placeholder-gray-400"
                            style={{ color: '#424242' }}
                            placeholder="Code"
                            value={newItem.code}
                            onChange={(e) => setNewItem({ ...newItem, code: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newItem.name.trim()) handleAddItem();
                            }}
                            onFocus={() => setIsInputRowActive(true)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="number"
                            className="w-full bg-transparent text-sm focus:outline-none placeholder-gray-400"
                            style={{ color: '#424242' }}
                            placeholder="1"
                            value={newItem.default_quantity}
                            onChange={(e) => setNewItem({ ...newItem, default_quantity: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newItem.name.trim()) handleAddItem();
                            }}
                            onFocus={() => setIsInputRowActive(true)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            className="w-full bg-transparent text-sm focus:outline-none placeholder-gray-400"
                            style={{ color: '#424242' }}
                            placeholder="Unit"
                            value={newItem.unit}
                            onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newItem.name.trim()) handleAddItem();
                            }}
                            onFocus={() => setIsInputRowActive(true)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <input
                            type="text"
                            className="w-full bg-transparent text-sm focus:outline-none placeholder-gray-400"
                            style={{ color: '#424242' }}
                            placeholder="$0.00"
                            value={newItem.unit_price}
                            onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newItem.name.trim()) handleAddItem();
                            }}
                            onFocus={() => setIsInputRowActive(true)}
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="relative inline-flex items-center">
                            <select
                              className="appearance-none pr-6 bg-transparent text-sm focus:outline-none cursor-pointer"
                              style={{ color: newItem.category ? '#424242' : '#9ca3af' }}
                              value={newItem.category}
                              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                              onFocus={() => setIsInputRowActive(true)}
                            >
                              <option value="">Category</option>
                              <option value="Material">Material</option>
                              <option value="Labor">Labor</option>
                              <option value="Equipment">Equipment</option>
                            </select>
                            <ChevronDown className="absolute right-0 w-4 h-4 pointer-events-none text-gray-400" />
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {newItem.name.trim() && (
                            <button
                              onClick={handleAddItem}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              title="Add item"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Preview row when input is active */}
                      {isInputRowActive && (
                        <tr className="opacity-40 pointer-events-none">
                          <td className="px-4 py-4">
                            <div className="w-5 h-5 rounded-md border-2 border-gray-200" />
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">Item name...</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">Code</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">1</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">Unit</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">$0.00</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">Category</span>
                          </td>
                          <td className="px-6 py-4"></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Empty state */}
              {!isLoading && catalogItems.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <p className="text-lg mb-2">No catalog items yet</p>
                  <p className="text-sm">Start adding items using the input row above</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Edit Panel */}
        <AnimatePresence>
          {showEditPanel && editingItem && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black bg-opacity-30 z-40"
                onClick={handleCancelEdit}
              />

              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
              >
                <div className="p-6 space-y-6">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                    <h2 className="text-lg font-semibold" style={{ color: '#1C1C1C' }}>
                      Edit Catalog Item
                    </h2>
                    <button
                      onClick={handleCancelEdit}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Item Name *
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ color: '#424242', border: '1px solid #e8e8e8' }}
                        placeholder="Enter item name"
                        value={editingItem.name || ""}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Code
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ color: '#424242', border: '1px solid #e8e8e8' }}
                        placeholder="Enter code"
                        value={editingItem.code || ""}
                        onChange={(e) => setEditingItem({ ...editingItem, code: e.target.value })}
                      />
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                          Default Quantity
                        </label>
                        <input
                          type="number"
                          className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ color: '#424242', border: '1px solid #e8e8e8' }}
                          placeholder="1"
                          value={editingItem.default_quantity || ""}
                          onChange={(e) => setEditingItem({ ...editingItem, default_quantity: parseInt(e.target.value, 10) || 1 })}
                        />
                      </div>

                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                          Unit
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ color: '#424242', border: '1px solid #e8e8e8' }}
                          placeholder="e.g., pcs, kg, m"
                          value={editingItem.unit || ""}
                          onChange={(e) => setEditingItem({ ...editingItem, unit: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Unit Price
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ color: '#424242', border: '1px solid #e8e8e8' }}
                        placeholder="$0.00"
                        value={editingItem.unit_price || ""}
                        onChange={(e) => setEditingItem({ ...editingItem, unit_price: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Category
                      </label>
                      <div className="relative">
                        <select
                          className="w-full appearance-none px-3 py-2 pr-10 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ color: '#424242', border: '1px solid #e8e8e8' }}
                          value={editingItem.category || ""}
                          onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                        >
                          <option value="">Select category</option>
                          <option value="Material">Material</option>
                          <option value="Labor">Labor</option>
                          <option value="Equipment">Equipment</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#1C1C1C' }} />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveEdit}
                      disabled={updateMutation.isPending}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 hover:opacity-90 text-white text-sm font-medium rounded-md transition-colors shadow-sm disabled:opacity-50"
                      style={{ backgroundColor: '#006de2' }}
                    >
                      {updateMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors border border-gray-300 shadow-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </MainLayout>
  );
}
